import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { OrganisationContext, UserContext } from '../../../context';
import {
  ChannelAppTabAction,
  NewsFeedScope,
  NewsFeedType,
  OrganisationTagType,
  StorySection,
  storyCardApi,
} from '../../../services';
import {
  Filter,
  FilterValue,
  NEWS_FEED_ADMIN_COOKIE_KEY,
  cookieOptions,
  optArrItem,
  useAppDispatch,
  useAppTabsLabel,
  useAuthorsFilter,
  useCardCategoryFilter,
  useCardStatusFilter,
  useCardTypeFilter,
  useDateRangeFilter,
  useFeedFollowBehaviorFilter,
  useFilter,
  usePinnedStoryId,
  useSourcesFilter,
  useTags,
  useTagsFilter,
} from '../../../shared';
import { getNewsFeed, getPinnedFeed } from '../../../slices';

import classes from './NewsFeedAdminRightSide.module.scss';

export const NewsFeedAdminRightSide: FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { hasInternalTags, hasExternalTags, hasExternalStatusTags } = useTags();

  const hasCategories = Boolean(useContext(OrganisationContext).organisation.categories.length);

  const [fetchAuthors] = storyCardApi.endpoints.storyCardAuthorsFiltersByChannelId.useLazyQuery();

  const [fetchSources] = storyCardApi.endpoints.storyCardSourcesFiltersByChannelId.useLazyQuery();

  const { getAppTabsLabel } = useAppTabsLabel();

  const { getPinnedStoryId } = usePinnedStoryId();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const [cookies, setCookie] = useCookies([NEWS_FEED_ADMIN_COOKIE_KEY]);

  const [filter, setFilter] = useState<FilterValue[]>(
    cookies[NEWS_FEED_ADMIN_COOKIE_KEY]?.filter ?? [
      {
        groupId: 'sections',
        groupItems: [{ id: StorySection.WEB_FEED, value: StorySection.WEB_FEED }],
      },
    ]
  );

  const { getGroupValue, getDateRangeValue } = useFilter();

  const { group: dateRangeGroup } = useDateRangeFilter({ filter });

  const { group: authorsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchAuthors,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: sourcesGroup } = useSourcesFilter({
    filter,
    fetchItemsQuery: fetchSources,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: categoryGroup } = useCardCategoryFilter({ filter });

  const { group: internalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.INTERNAL],
  });

  const { group: externalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL],
  });

  const { group: externalStatusTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL_STATUS],
  });

  const { group: cardTypeGroup } = useCardTypeFilter();

  const { group: cardStatusGroup } = useCardStatusFilter();

  const { group: feedFollowBehaviorGroup } = useFeedFollowBehaviorFilter();

  const pinnedSectionItems = useMemo(
    () =>
      [
        { section: StorySection.PINNED, appTabAction: ChannelAppTabAction.PINNED_STORY },
        { section: StorySection.PINNED_2, appTabAction: ChannelAppTabAction.PINNED_STORY_2 },
        { section: StorySection.PINNED_3, appTabAction: ChannelAppTabAction.PINNED_STORY_3 },
        { section: StorySection.PINNED_4, appTabAction: ChannelAppTabAction.PINNED_STORY_4 },
        { section: StorySection.PINNED_5, appTabAction: ChannelAppTabAction.PINNED_STORY_5 },
        {
          section: StorySection.PINNED_ORG,
          appTabAction: ChannelAppTabAction.PINNED_STORY,
          labelSuffix: t('pinned.at-organisation'),
        },
      ]
        .filter(({ section }) => getPinnedStoryId(section))
        .map(({ section, appTabAction, labelSuffix }) => ({
          id: section,
          label: [getAppTabsLabel(appTabAction), labelSuffix].filter(Boolean).join(' '),
          value: section,
        })),
    [getAppTabsLabel, getPinnedStoryId, t]
  );

  const groups = useMemo(
    () => [
      {
        id: 'sections',
        title: t('common.sections'),
        items: [
          {
            id: StorySection.WEB_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.web')}`,
            value: StorySection.WEB_FEED,
          },
          {
            id: StorySection.MOBILE_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.mobile')}`,
            value: StorySection.MOBILE_FEED,
          },
          ...pinnedSectionItems,
        ],
      },
      ...optArrItem(hasInternalTags, internalTagsGroup),
      ...optArrItem(hasExternalTags, externalTagsGroup),
      ...optArrItem(hasExternalStatusTags, externalStatusTagsGroup),
      ...optArrItem(hasCategories, categoryGroup),
      authorsGroup,
      sourcesGroup,
      dateRangeGroup,
      cardTypeGroup,
      cardStatusGroup,
      feedFollowBehaviorGroup,
    ],
    [
      authorsGroup,
      cardStatusGroup,
      cardTypeGroup,
      categoryGroup,
      dateRangeGroup,
      externalStatusTagsGroup,
      feedFollowBehaviorGroup,
      externalTagsGroup,
      getAppTabsLabel,
      hasCategories,
      hasExternalStatusTags,
      hasExternalTags,
      hasInternalTags,
      internalTagsGroup,
      pinnedSectionItems,
      sourcesGroup,
      t,
    ]
  );

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      const commonIdProps = { filter, multiCheck: true, propToMap: 'id' };

      const authorId = getGroupValue({ ...commonIdProps, groupId: authorsGroup.id });

      const sourceName = getGroupValue({ ...commonIdProps, groupId: sourcesGroup.id });

      const categoryId = getGroupValue({ ...commonIdProps, groupId: categoryGroup.id });

      const internalTagsId = getGroupValue({ ...commonIdProps, groupId: internalTagsGroup.id });

      const externalTagsId = getGroupValue({ ...commonIdProps, groupId: externalTagsGroup.id });

      const externalStatusTagsId = getGroupValue({
        ...commonIdProps,
        groupId: externalStatusTagsGroup.id,
      });

      const section = getGroupValue({ filter, groupId: 'sections' });

      const pinnedStoryId = getPinnedStoryId(section);

      const { dateRange, from, to } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      const storyCardType = getGroupValue({ filter, groupId: cardTypeGroup.id, multiCheck: true });

      const status = getGroupValue({ filter, groupId: cardStatusGroup.id, multiCheck: true });

      const feedFollowBehavior = getGroupValue({
        filter,
        groupId: feedFollowBehaviorGroup.id,
        multiCheck: true,
      });

      const common = {
        ...(authorId?.length && { authorId }),
        ...(sourceName?.length && { sourceName }),
        ...(categoryId?.length && { categoryId }),
        ...((internalTagsId?.length || externalTagsId?.length || externalStatusTagsId?.length) && {
          organisationTags: { internalTagsId, externalTagsId, externalStatusTagsId },
        }),
        ...(section === StorySection.HORIZONTAL && {
          feedType: [NewsFeedType.HORIZONTAL_STORY_FEED],
        }),
        ...(section === StorySection.WEB_FEED && { scope: NewsFeedScope.WEB_FEED }),
        ...(section === StorySection.MOBILE_FEED && { scope: NewsFeedScope.MOBILE_FEED }),
        ...(storyCardType?.length && { storyCardType }),
        ...(status?.length && { status }),
      };

      const requestFilterToSave = {
        ...common,
        ...(pinnedStoryId && { pinnedStorySection: section }),
        ...(feedFollowBehavior?.length && { feedFollowBehavior }),
        dateRange,
      };

      const requestFilter = { ...common, ...(from && { from }), ...(to && { to }) };

      setCookie(
        NEWS_FEED_ADMIN_COOKIE_KEY,
        { requestFilter: requestFilterToSave, filter },
        cookieOptions()
      );

      setFilter(filter);

      if (pinnedStoryId) {
        dispatch(getPinnedFeed({ storyId: pinnedStoryId, filter: requestFilter }));

        return;
      }

      dispatch(
        getNewsFeed({
          channelId,
          filter: {
            ...requestFilter,
            ...(feedFollowBehavior?.length && { feedFollowBehavior }),
          },
        })
      );
    },
    [
      authorsGroup.id,
      cardStatusGroup.id,
      cardTypeGroup.id,
      categoryGroup.id,
      channelId,
      dateRangeGroup.id,
      dispatch,
      externalStatusTagsGroup.id,
      externalTagsGroup.id,
      feedFollowBehaviorGroup.id,
      getDateRangeValue,
      getGroupValue,
      getPinnedStoryId,
      internalTagsGroup.id,
      setCookie,
      sourcesGroup.id,
    ]
  );

  return (
    <div className={classes['news-feed-admin-right-side']}>
      <Filter filter={filter} groups={groups} onChange={filterChange} resetPageScrollOnChange />
    </div>
  );
};
